import { ChangeDetectionStrategy, ChangeDetectorRef, Component, signal, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { PayErrorResponse } from 'projects/login/src/app/services/auth/models/pay-error-response';
import { TokenService } from 'projects/login/src/app/services/auth/token.service';
import { TwoFaService } from 'projects/login/src/app/services/auth/two-fa.service';
import { RedirectService } from 'projects/login/src/app/services/redirect/redirect.service';
import { ButtonPrimaryComponent } from '../../button-primary/button-primary.component';
import { ErrorBadgeComponent } from '../../error-badge/error-badge.component';
import { PhoneInputComponent } from '../../phone-input/phone-input.component';
import { PinEntryComponent } from '../../pin-entry/pin-entry.component';

@Component({
  selector: 'pay-login-setup-two-factor-auth-sms',
  imports: [ButtonPrimaryComponent, PinEntryComponent, TranslateModule, PhoneInputComponent, ReactiveFormsModule, ErrorBadgeComponent],
  templateUrl: './setup-two-factor-auth-sms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetupTwoFactorAuthSmsComponent {
  private readonly _twoFaService = inject(TwoFaService);

  smsIsSent = signal(false);
  retryInSeconds = signal(30);
  phoneNumber = signal('');
  loading = signal(false);
  error = signal<PayErrorResponse | undefined>(undefined);

  formSendSms = new FormGroup({
    phoneNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3)],
    }),
  });

  @ViewChild('pinEntryComponent') pinEntry!: PinEntryComponent;

  sendSms() {
    if (this.formSendSms.invalid) {
      return;
    }
    this.error.set(undefined);
    this.loading.set(true);
    this.phoneNumber.set(this.formSendSms.controls.phoneNumber.value!);

    this._twoFaService
      .twoFaSetup({
        method: 'sms',
        verificationPhoneNumber: this.phoneNumber().replaceAll(' ', ''),
      })
      .subscribe({
        next: () => {
          this.sendSmsToUser();
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err.error as PayErrorResponse);
          this.loading.set(false);
        },
      });
  }

  sendSmsToUser() {
    this.retryInSeconds.set(30);

    const intervalId = setInterval(() => {
      this.retryInSeconds.set(this.retryInSeconds() - 1);
      if (this.retryInSeconds() <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    this._twoFaService.trigger().subscribe({
      next: () => {
        this.smsIsSent.set(true);
      },
      error: (err) => {
        this.error.set(err.error as PayErrorResponse);
        this.loading.set(false);
      },
    });
  }

  submitPinEntry() {
    this.pinEntry.disabled.set(true);
    this.loading.set(true);

    this._twoFaService
      .verify({
        value: this.pinEntry.pinValues().join(''),
        rememberFor: null,
      })
      .subscribe({
        next: (response) => {
          window.location.href = response.redirectUrl ?? window.location.protocol + '//' + window.location.host;
        },
        error: (err) => {
          this.error.set(err.error as PayErrorResponse);
          this.loading.set(false);
          this.pinEntry.disabled.set(false);
        },
      });
  }
}
